import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Grid,
  TextField,
  FormLabel,
  FormHelperText,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
} from "@material-ui/core";
import { useFormik } from "formik";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import * as Yup from "yup";

const UserCreditTransferComponent = ({
  //  👇  Credit Transfer Dialog  👇  //
  selectedCompanyUser,
  onCreditTransferCloseClick,
  //  👇  Credit Transfer Confirmation Dialog  👇  //
  openCreditTransferConfirmation,
  onCreditTransferConfirmationClick,
  onCreditTransferConfirmSubmitClick,
  isSubmitting,
  onCreditTransferConfirmationCloseClick,
  //  👇  Credit Transfer Complete Dialog  👇  //
  completeDialogErrors,
  openCompleteDialog,
  onCompleteDialogClose,
}) => {
  const actions = ["credit", "debit", "seqRangeReimbursed"];

  // Range type tab state (0 = Sequence Number, 1 = Serial Number)
  const [rangeTypeTab, setRangeTypeTab] = useState(0);

  // State for multiple ranges
  const [seqRanges, setSeqRanges] = useState([{ fromSeq: "", toSeq: "" }]);
  const [snRanges, setSnRanges] = useState([{ fromSn: "", toSn: "" }]);

  const calculateNewBalance = (amount, action) => {
    const currentBalance = selectedCompanyUser?.creditInfo?.amount || 0;
    if (action === "credit") {
      return currentBalance + amount;
    }
    if (action === "debit") {
      return currentBalance - amount;
    }
    if (action === "seqRangeReimbursed") {
      return currentBalance + amount;
    }
    return currentBalance;
  };

  const addSeqRange = () => {
    setSeqRanges([...seqRanges, { fromSeq: "", toSeq: "" }]);
  };

  const removeSeqRange = (index) => {
    if (seqRanges.length > 1) {
      const newRanges = seqRanges.filter((_, i) => i !== index);
      setSeqRanges(newRanges);
    }
  };

  const updateSeqRange = (index, field, value) => {
    const newRanges = seqRanges.map((range, i) =>
      i === index ? { ...range, [field]: value } : range
    );
    setSeqRanges(newRanges);
  };

  const addSnRange = () => {
    setSnRanges([...snRanges, { fromSn: "", toSn: "" }]);
  };

  const removeSnRange = (index) => {
    if (snRanges.length > 1) {
      const newRanges = snRanges.filter((_, i) => i !== index);
      setSnRanges(newRanges);
    }
  };

  const updateSnRange = (index, field, value) => {
    const newRanges = snRanges.map((range, i) =>
      i === index ? { ...range, [field]: value } : range
    );
    setSnRanges(newRanges);
  };

  const calculateTotalFromSeqRanges = () =>
    seqRanges.reduce((total, range) => {
      const from = parseInt(range.fromSeq, 10);
      const to = parseInt(range.toSeq, 10);
      if (!Number.isNaN(from) && !Number.isNaN(to) && to >= from) {
        return total + (to - from + 1);
      }
      return total;
    }, 0);

  // Calculate quantity from serial number ranges by extracting numeric suffix
  const calculateTotalFromSnRanges = () =>
    snRanges.reduce((total, range) => {
      if (!range.fromSn || !range.toSn) return total;
      // Extract numeric part from serial numbers (e.g., PETWORLD00330 -> 330)
      const fromMatch = range.fromSn.match(/(\d+)$/);
      const toMatch = range.toSn.match(/(\d+)$/);
      if (fromMatch && toMatch) {
        const fromNum = parseInt(fromMatch[1], 10);
        const toNum = parseInt(toMatch[1], 10);
        if (
          !Number.isNaN(fromNum) &&
          !Number.isNaN(toNum) &&
          toNum >= fromNum
        ) {
          return total + (toNum - fromNum + 1); // +1 because inclusive on both ends
        }
      }
      return total;
    }, 0);

  const validateSeqRanges = () => {
    const invalidRange = seqRanges.find((range) => {
      const from = parseInt(range.fromSeq, 10);
      const to = parseInt(range.toSeq, 10);
      return (
        !range.fromSeq ||
        !range.toSeq ||
        Number.isNaN(from) ||
        Number.isNaN(to) ||
        to < from
      );
    });
    if (!invalidRange) {
      return null;
    }
    const from = parseInt(invalidRange.fromSeq, 10);
    const to = parseInt(invalidRange.toSeq, 10);
    if (!invalidRange.fromSeq || !invalidRange.toSeq) {
      return "All ranges must be filled";
    }
    if (Number.isNaN(from) || Number.isNaN(to)) {
      return "Invalid number in ranges";
    }
    if (to < from) {
      return "TO must be >= FROM in all ranges";
    }
    return null;
  };

  const validateSnRanges = () => {
    const invalidRange = snRanges.find((range) => !range.fromSn || !range.toSn);
    if (invalidRange) {
      return "All serial number ranges must be filled";
    }
    return null;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      balance: selectedCompanyUser?.creditInfo
        ? selectedCompanyUser?.creditInfo.amount
        : 0,
      creditTransferAction: "credit",
      amount: 0,
      confirmAmount: 0,
      newBalance: 0,
      remark: "",
    },
    initialErrors: {
      amount: "Positive Amount Number is required",
      confirmAmount: "Positive Confirm Amount Number is required",
      remark: "Remark is required",
    },
    validationSchema: Yup.object().shape({
      creditTransferAction: Yup.string().required("Action is required"),
      amount: Yup.number()
        .min(1, "Positive Amount Number is required")
        .required("Amount is required")
        .typeError("Invalid number"),
      confirmAmount: Yup.number()
        .oneOf([Yup.ref("amount"), null], "Amount must match")
        .required("Confirm Amount is required")
        .typeError("Invalid number"),
      newBalance: Yup.number().min(0),
      remark: Yup.string().required("Remark is required"),
    }),
    onSubmit: (values) => {
      // Add ranges to values before submitting
      const submitValues = {
        ...values,
        rangeType: rangeTypeTab === 0 ? "sequence" : "serial",
        seqRanges: rangeTypeTab === 0 ? seqRanges : [],
        snRanges: rangeTypeTab === 1 ? snRanges : [],
      };
      onCreditTransferConfirmSubmitClick(submitValues, formik);
    },
  });

  // Reset ranges when action changes
  useEffect(() => {
    if (formik.values.creditTransferAction !== "seqRangeReimbursed") {
      setSeqRanges([{ fromSeq: "", toSeq: "" }]);
      setSnRanges([{ fromSn: "", toSn: "" }]);
      setRangeTypeTab(0);
    }
  }, [formik.values.creditTransferAction]);

  const handleInputChange = (event) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      formik.setFieldValue(event.target.name, "");
      return;
    }

    const pattern = /^[0-9]*$/;

    if (!pattern.test(inputValue)) {
      event.preventDefault();
      return;
    }

    const parsedValue = parseInt(inputValue, 10);

    if (!Number.isNaN(parsedValue)) {
      const finalValue = parsedValue === 0 ? 1 : parsedValue;
      formik.setFieldValue(event.target.name, finalValue);
    }
  };

  useEffect(
    () => {
      const newBalance = calculateNewBalance(
        formik.values.amount,
        formik.values.creditTransferAction
      );
      formik.setFieldValue("newBalance", newBalance);
    },
    [
      formik.values.amount,
      formik.values.creditTransferAction,
      formik.values.remark,
    ],
    []
  );

  // Auto-calculate amount from sequence ranges for seqRangeReimbursed
  useEffect(() => {
    if (
      formik.values.creditTransferAction === "seqRangeReimbursed" &&
      rangeTypeTab === 0
    ) {
      const calculatedAmount = calculateTotalFromSeqRanges();
      formik.setFieldValue("amount", calculatedAmount);
      formik.setFieldValue("confirmAmount", calculatedAmount);
    }
  }, [seqRanges, formik.values.creditTransferAction, rangeTypeTab]);

  // Auto-calculate amount from serial number ranges for seqRangeReimbursed
  useEffect(() => {
    if (
      formik.values.creditTransferAction === "seqRangeReimbursed" &&
      rangeTypeTab === 1
    ) {
      const calculatedAmount = calculateTotalFromSnRanges();
      formik.setFieldValue("amount", calculatedAmount);
      formik.setFieldValue("confirmAmount", calculatedAmount);
    }
  }, [snRanges, formik.values.creditTransferAction, rangeTypeTab]);

  const resetAndClose = () => {
    onCreditTransferCloseClick();
    formik.resetForm();
    setSeqRanges([{ fromSeq: "", toSeq: "" }]);
    setSnRanges([{ fromSn: "", toSn: "" }]);
    setRangeTypeTab(0);
  };

  const getRangeValidationError = () => {
    if (rangeTypeTab === 0) {
      return validateSeqRanges();
    }
    return validateSnRanges();
  };

  return (
    <>
      <Dialog
        open={!!selectedCompanyUser}
        onClose={resetAndClose}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth
      >
        <form>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "1rem",
            }}
          >
            <Typography
              id="scroll-dialog-title"
              sx={{ fontWeight: "500", fontSize: "large" }}
            >
              {`${selectedCompanyUser?.username} Credit Transfer`}
            </Typography>
            <Typography
              id="scroll-dialog-title"
              sx={{ cursor: "pointer" }}
              onClick={resetAndClose}
            >
              ✖️
            </Typography>
          </Box>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Left side - Current Balance */}
                  <Grid item>
                    <Typography
                      fontFamily="Roboto"
                      display="inline"
                      marginRight={1}
                    >
                      Current Balance:
                    </Typography>
                    <Typography
                      fontFamily="Roboto"
                      display="inline"
                      variant="h4"
                    >
                      {selectedCompanyUser?.creditInfo
                        ? formik.values.balance
                        : "0 (No Wallet)"}
                    </Typography>
                  </Grid>

                  {/* Right side - Action */}
                  <Grid item>
                    <FormLabel
                      style={{
                        color: formik.errors.creditTransferAction
                          ? "#d32f2f"
                          : "inherit",
                        fontFamily: "Roboto",
                        display: "inline",
                        marginRight: "1rem",
                      }}
                    >
                      Transfer Type:
                    </FormLabel>

                    <ToggleButtonGroup
                      name="creditTransferAction"
                      value={formik.values.creditTransferAction}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          formik.setFieldValue(
                            "creditTransferAction",
                            newValue
                          );
                        }
                      }}
                    >
                      {actions.map((action) => (
                        <ToggleButton
                          key={action}
                          value={action}
                          disabled={
                            formik.isSubmitting ||
                            (action === "debit" &&
                              !selectedCompanyUser?.creditInfo)
                          }
                        >
                          {action === "seqRangeReimbursed"
                            ? "+ Reimbursed"
                            : (action === "debit" ? "- " : "+ ") +
                              action.charAt(0).toUpperCase() +
                              action.slice(1)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    <FormHelperText
                      error={!!formik.errors.creditTransferAction}
                    >
                      {formik.errors.creditTransferAction}
                    </FormHelperText>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="text"
                  value={formik.values.amount}
                  onChange={handleInputChange}
                  error={!!formik.errors.amount}
                  helperText={
                    formik.values.creditTransferAction === "seqRangeReimbursed"
                      ? rangeTypeTab === 0
                        ? "Auto-calculated from sequence range"
                        : "Auto-calculated from serial number range"
                      : formik.errors.amount
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly:
                      formik.values.creditTransferAction ===
                      "seqRangeReimbursed",
                  }}
                  disabled={
                    formik.values.creditTransferAction === "seqRangeReimbursed"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Confirm Amount"
                  name="confirmAmount"
                  type="text"
                  value={formik.values.confirmAmount}
                  onChange={handleInputChange}
                  error={!!formik.errors.confirmAmount}
                  helperText={
                    formik.values.creditTransferAction === "seqRangeReimbursed"
                      ? rangeTypeTab === 0
                        ? "Auto-calculated from sequence range"
                        : "Auto-calculated from serial number range"
                      : formik.errors.confirmAmount
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly:
                      formik.values.creditTransferAction ===
                      "seqRangeReimbursed",
                  }}
                  disabled={
                    formik.values.creditTransferAction === "seqRangeReimbursed"
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ color: formik.values.newBalance < 0 && "#d32f2f" }}
              >
                <Typography
                  marginBottom={1}
                  fontFamily="Roboto"
                  display="inline"
                >
                  New Balance:{" "}
                </Typography>
                <Typography variant="h4" fontFamily="Roboto" display="inline">
                  {Number(formik.values.newBalance).toLocaleString()}
                </Typography>
                {formik.errors.newBalance && (
                  <Typography
                    variant="h4"
                    sx={{
                      marginLeft: "1rem",
                      marginTop: "5px",
                      fontFamily: "sans-serif",
                      color: "#d32f2f",
                      fontSize: "0.8rem",
                    }}
                  >
                    {formik.errors.newBalance}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Remark"
                  name="remark"
                  multiline
                  rows={4}
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  error={!!formik.errors.remark}
                  helperText={formik.errors.remark}
                />
              </Grid>

              {/* Range Type Tabs and Ranges */}
              {formik.values.creditTransferAction === "seqRangeReimbursed" && (
                <Grid item xs={12}>
                  <Tabs
                    value={rangeTypeTab}
                    onChange={(e, newValue) => setRangeTypeTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ marginBottom: 16 }}
                  >
                    <Tab label="Sequence Number" />
                    <Tab label="Serial Number" />
                  </Tabs>

                  {/* Sequence Number Ranges */}
                  {rangeTypeTab === 0 && (
                    <>
                      {seqRanges.map((range, index) => {
                        const from = parseInt(range.fromSeq, 10);
                        const to = parseInt(range.toSeq, 10);
                        const rangeQty =
                          !Number.isNaN(from) && !Number.isNaN(to) && to >= from
                            ? to - from + 1
                            : 0;
                        return (
                          <Grid
                            container
                            spacing={1}
                            key={`seq-${index}`}
                            alignItems="center"
                            style={{ marginBottom: 8 }}
                          >
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="FROM"
                                placeholder="Enter seq number"
                                type="text"
                                value={range.fromSeq}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || /^[0-9]*$/.test(val)) {
                                    updateSeqRange(index, "fromSeq", val);
                                  }
                                }}
                                error={
                                  range.fromSeq &&
                                  range.toSeq &&
                                  parseInt(range.toSeq, 10) <
                                    parseInt(range.fromSeq, 10)
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item>
                              <Typography>-</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="TO"
                                placeholder="Enter seq number"
                                type="text"
                                value={range.toSeq}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || /^[0-9]*$/.test(val)) {
                                    updateSeqRange(index, "toSeq", val);
                                  }
                                }}
                                error={
                                  range.fromSeq &&
                                  range.toSeq &&
                                  parseInt(range.toSeq, 10) <
                                    parseInt(range.fromSeq, 10)
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2" color="textSecondary">
                                Qty: {rangeQty}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <IconButton
                                size="small"
                                onClick={() => removeSeqRange(index)}
                                disabled={seqRanges.length === 1}
                              >
                                <RemoveCircleOutlineIcon />
                              </IconButton>
                              <IconButton size="small" onClick={addSeqRange}>
                                <AddCircleOutlineIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        );
                      })}
                      {validateSeqRanges() && (
                        <Typography variant="caption" color="error">
                          {validateSeqRanges()}
                        </Typography>
                      )}
                    </>
                  )}

                  {/* Serial Number Ranges */}
                  {rangeTypeTab === 1 && (
                    <>
                      {snRanges.map((range, index) => {
                        // Calculate quantity for this range
                        let rangeQty = 0;
                        if (range.fromSn && range.toSn) {
                          const fromMatch = range.fromSn.match(/(\d+)$/);
                          const toMatch = range.toSn.match(/(\d+)$/);
                          if (fromMatch && toMatch) {
                            const fromNum = parseInt(fromMatch[1], 10);
                            const toNum = parseInt(toMatch[1], 10);
                            if (
                              !Number.isNaN(fromNum) &&
                              !Number.isNaN(toNum) &&
                              toNum >= fromNum
                            ) {
                              rangeQty = toNum - fromNum + 1;
                            }
                          }
                        }
                        return (
                          <Grid
                            container
                            spacing={1}
                            key={`sn-${index}`}
                            alignItems="center"
                            style={{ marginBottom: 8 }}
                          >
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="FROM"
                                placeholder="e.g. PETWORLD00001"
                                type="text"
                                value={range.fromSn}
                                onChange={(e) =>
                                  updateSnRange(
                                    index,
                                    "fromSn",
                                    e.target.value.toUpperCase()
                                  )
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item>
                              <Typography>-</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="TO"
                                placeholder="e.g. PETWORLD00100"
                                type="text"
                                value={range.toSn}
                                onChange={(e) =>
                                  updateSnRange(
                                    index,
                                    "toSn",
                                    e.target.value.toUpperCase()
                                  )
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2" color="textSecondary">
                                Qty: {rangeQty}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <IconButton
                                size="small"
                                onClick={() => removeSnRange(index)}
                                disabled={snRanges.length === 1}
                              >
                                <RemoveCircleOutlineIcon />
                              </IconButton>
                              <IconButton size="small" onClick={addSnRange}>
                                <AddCircleOutlineIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        );
                      })}
                      {validateSnRanges() && (
                        <Typography variant="caption" color="error">
                          {validateSnRanges()}
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button
              variant="contained"
              type="button"
              onClick={onCreditTransferConfirmationClick}
              disabled={
                !!Object.keys(formik.errors).length ||
                (formik.values.creditTransferAction === "seqRangeReimbursed" &&
                  !!getRangeValidationError())
              }
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={openCreditTransferConfirmation}
        onClose={onCreditTransferConfirmationCloseClick}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "25vw",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography
            id="scroll-dialog-title"
            sx={{ fontWeight: "500", fontSize: "large" }}
          >
            Credit Transfer Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <Typography>Username:</Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedCompanyUser?.username}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <Typography>Current Balance:</Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {Number(formik.values.balance).toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <Typography>Amount:</Typography>
            <Typography
              sx={{
                fontWeight: "bold",
                color:
                  formik.values.creditTransferAction === "debit"
                    ? "red"
                    : "green",
              }}
            >
              {`${
                formik.values.creditTransferAction === "debit" ? "- " : "+ "
              }${Number(formik.values.amount).toLocaleString()}`}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <Typography>New Balance:</Typography>
            <Typography
              sx={{
                fontWeight: "bold",
                color:
                  formik.values.creditTransferAction === "debit"
                    ? "red"
                    : "green",
              }}
            >
              {Number(formik.values.newBalance).toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 2, justifyContent: "end" }}>
          <Button
            variant="contained"
            color="error"
            onClick={onCreditTransferConfirmationCloseClick}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={formik.handleSubmit}
          >
            {isSubmitting ? (
              <>
                {" Confirming..."}
                &nbsp;&nbsp;
                <CircularProgress color="inherit" size="1.5rem" />
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* COMPLETE DIALOG */}
      <Dialog
        open={openCompleteDialog}
        onClose={onCompleteDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent
          sx={{
            backgroundColor:
              completeDialogErrors.length > 0 ? "#ff3333" : "#4BB543",
            color: "white",
          }}
        >
          <Box
            align="center"
            margin={3}
            sx={{
              borderRadius: "1000px",
              width: "4rem",
              height: "4rem",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginX: "auto",
            }}
          >
            <Typography
              variant="h1"
              align="center"
              margin={3}
              color={completeDialogErrors.length > 0 ? "#ff3333" : "#4BB543"}
            >
              {completeDialogErrors.length > 0 ? "✘" : "✔"}
            </Typography>
          </Box>
          <Typography variant="h3" align="center" margin={3}>
            {`Credit Transfer ${
              completeDialogErrors.length > 0 ? "Failed" : "Complete"
            }`}
          </Typography>
          <Typography variant="body2" align="center" margin={3}>
            ( Click outside to close )
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserCreditTransferComponent;
